import { msg } from '@lingui/core/macro';

import { CREDIT_NOTE_STATUS } from '@/modules/credit-note/public';
import { defineFilters } from '@/shared/filters/define-filters';
import { defineEntitySource } from '@/shared/form/entity-source';
import { decimalToCents, parseMoneyInput } from '@/shared/format/money';
import { getCurrentTenant } from '@/shared/http/client';

// Filtros da aba Internet Banking, declarados uma vez (P01). Leve: entra no `validateSearch` da rota.

/** `selectOptionsYesOrNo` (BankAccountApprovalsFilters.tsx:20-23): o fio leva `'true'`/`'false'` como texto. */
export const YES_NO_OPTIONS = [
  { value: 'true', label: msg`Sim` },
  { value: 'false', label: msg`Não` },
] as const;

/**
 * `availableFilters` da aba (BankAccountListContainer.tsx:302-332), na ordem e com os rótulos de lá. Cada um é um
 * Sim/Não; "Não" também vai ao fio (`filterX=false`), e o fio segue a ordem em que a pessoa aplicou (filterContext).
 */
export const internetBankingFilters = defineFilters([
  { key: 'filterOnlyMine', type: 'select', label: msg`Somente minhas solicitações`, options: YES_NO_OPTIONS },
  { key: 'filterAlreadyApproved', type: 'select', label: msg`Somente solicitações que já respondi`, options: YES_NO_OPTIONS },
  { key: 'filterFinished', type: 'select', label: msg`Somente solicitações já concluídas`, options: YES_NO_OPTIONS },
  { key: 'filterOnlyImOperator', type: 'select', label: msg`Apenas contas que sou operador`, options: YES_NO_OPTIONS },
  { key: 'filterExpired', type: 'select', label: msg`Somente solicitações já expiradas`, options: YES_NO_OPTIONS },
]);

// ─── Operações (R6) ────────────────────────────────────────────────────────────────────────────────

const reference = () => import('@/shared/reference/reference.api');
const people = () => import('@/modules/person/public');

/** "Correspondente": a lista de `GET /Tenant` do `useTenant` (CreditNoteApprovalsFilters.tsx:53-61), filtrada pelo nome. */
export const creditNoteTenantSource = defineEntitySource<{ readonly id: string; readonly name: string }>({
  key: ['approvals', 'filter', 'tenant'],
  search: async (query, signal) => {
    const needle = query.trim().toLowerCase();
    return (await (await reference()).listTenantOptions(signal)).filter((item) => item.name.toLowerCase().includes(needle));
  },
  byId: async (id, signal) => (await (await reference()).listTenantOptions(signal)).find((item) => item.id === id),
  toOption: (item) => ({ value: item.id, label: item.name }),
});

type PersonOption = { readonly id: string; readonly name: string };

/** "Tomador": `GET /Person` do `usePersonsList` com o tenant de quem vê; a opção é só o nome (CreditNoteApprovalsFilters.tsx:22-34). */
export const creditNotePersonSource = defineEntitySource<PersonOption>({
  key: ['approvals', 'filter', 'person'],
  scope: () => getCurrentTenant() ?? null,
  search: async (query, signal) => (await (await people()).listPersonOptions({ searchString: query || undefined, tenant: getCurrentTenant() }, signal)).data,
  toOption: (item) => ({ value: item.id, label: item.name }),
});

/** Sem `Person:Read` o legado não busca (`enabled: hasReadPermission`, personContext.tsx:43-47): a lista fica vazia, sem chamada. */
export const creditNotePersonSourceDenied = defineEntitySource<PersonOption>({
  key: ['approvals', 'filter', 'person', 'denied'],
  search: () => Promise.resolve([]),
  toOption: (item) => ({ value: item.id, label: item.name }),
});

/**
 * `CurrencyFormField` grava centavos (Currency.ts:19-27): "1.234,56" → `123456`. Zero não é filtro: `addFilter` descarta
 * valor falso (`if (!filterValue) return`, filterContext.tsx:252).
 */
export function centsFilterParam(value: string): number | undefined {
  const decimal = parseMoneyInput(value);
  if (decimal === null) return undefined;
  const cents = decimalToCents(decimal);
  return cents === 0 ? undefined : cents;
}

/** O que um `<input type="number">` aceita: o legado manda o texto como foi digitado (CreditNoteApprovalsFilters.tsx:75-80). */
const HTML_NUMBER = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;

/** "Número da operação": o valor do campo numérico do legado, sem mexer; o que o campo não aceita fica de fora. */
export function operationNumberParam(value: string): string | undefined {
  return HTML_NUMBER.test(value) ? value : undefined;
}

/**
 * O que a barra e o agente aplicam passa pelo mesmo crivo do `addFilter` do legado: valor que não vira filtro (zero, texto
 * que o campo numérico não aceita) sai do patch e não vira etiqueta.
 */
export function legacyFilterPatch(patch: Readonly<Record<string, unknown>>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...patch };
  for (const key of ['maxValue', 'minValue'] as const) {
    if (typeof out[key] === 'string' && centsFilterParam(out[key]) === undefined) out[key] = undefined;
  }
  if (typeof out['creditNoteNo'] === 'string' && operationNumberParam(out['creditNoteNo']) === undefined) out['creditNoteNo'] = undefined;
  return out;
}

/**
 * `availableFilters` da tela (CreditNoteApprovListWithFilters.tsx:43-75), na ordem do "Filtrar por"
 * (CreditNoteApprovalsFilters.tsx:43-51). "Data de início" é um período na tela, mas o legado só manda o início: o
 * `dateRangeName` é a própria chave e o fim é sobrescrito (filterContext.tsx:112-126). Fiel (U50), com `sendTo: false`.
 */
const creditNoteFiltersWith = (person: typeof creditNotePersonSource) =>
  defineFilters([
    { key: 'status', type: 'status', label: msg`Status`, options: CREDIT_NOTE_STATUS, pinned: true },
    { key: 'personId', type: 'entity', label: msg`Tomador`, source: person, placeholder: msg`Filtrar por tomador` },
    { key: 'initialDate', to: 'finalDate', type: 'dateRange', label: msg`Data de início`, fromLabel: msg`Filtrar por data inicial`, toLabel: msg`Filtrar por data final`, sendTo: false },
    { key: 'maxValue', type: 'text', label: msg`Valor máximo`, placeholder: msg`Em reais, como 50.000,00`, max: 20, toParam: centsFilterParam },
    { key: 'minValue', type: 'text', label: msg`Valor mínimo`, placeholder: msg`Em reais, como 1.000,00`, max: 20, toParam: centsFilterParam },
    { key: 'tenant', type: 'entity', label: msg`Correspondente`, source: creditNoteTenantSource, placeholder: msg`Filtrar por correspondente` },
    { key: 'creditNoteNo', type: 'text', label: msg`Número da operação`, placeholder: msg`Filtrar por número da operação`, max: 12, toParam: operationNumberParam },
  ]);

export const creditNoteApprovalFilters = creditNoteFiltersWith(creditNotePersonSource);
/** Os mesmos filtros para quem não tem `Person:Read`: o "Tomador" continua, com a lista vazia. */
export const creditNoteApprovalFiltersWithoutPerson = creditNoteFiltersWith(creditNotePersonSourceDenied);
